import { useEffect, useState } from "preact/hooks";
import { recallBoard } from "./board-cache";
import { onLiveChange } from "./live";
import { useOpenCount } from "./openCount";
import { isFlashActive } from "../shared/flash.ts";
import { peonMood, type PeonMoodName } from "../shared/peon-mood.ts";
import type { PeonMood } from "./peon-art.ts";

/**
 * The header peon's mood on the web (docs/peons.md "Moods", shared/peon-mood.ts), behind
 * `peonMoodsUI`. Reads what the page already knows: the open board's count (src/openCount.ts), the
 * gold rush from the open board's last summary (src/board-cache.ts), and the last real change the
 * live channel heard on this device (src/live.ts onLiveChange). Re-decided every 15 seconds and on
 * any of those changing, so an hour of quiet is noticed within a few seconds of the hour.
 */
const RECHECK_MS = 15_000;

/** When the board last changed on this device: the page's start, then every live change. */
let lastChangeAt = Date.now();

export function usePeonMoodNow(enabled: boolean): PeonMoodName | null {
  const openCount = useOpenCount();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const tick = () => setNow(Date.now());
    const stopLive = onLiveChange(() => {
      lastChangeAt = Date.now();
      tick();
    });
    const timer = setInterval(tick, RECHECK_MS);
    tick();
    return () => {
      clearInterval(timer);
      stopLive();
    };
  }, [enabled]);
  if (!enabled) return null;
  let flashOn = false;
  try {
    flashOn = isFlashActive(recallBoard("open")?.flash?.active ?? null, now);
  } catch {
    flashOn = false;
  }
  return peonMood({ openCount, flashOn, lastChangeAt, now });
}

/** The mood sheet drawn over a base portrait (scripts/make-peon-moods.mjs): 3 by 5 cells of 64. */
export function peonMoodSheet(base: PeonMood): { url: string; width: number; height: number } {
  return { url: `/ui/peon-moods-${base}-v1.png`, width: 64 * 3, height: 64 * 5 };
}
